import { motion } from "framer-motion";
import { useI18n } from "../../i18n/LanguageProvider";
import { Container, SectionLabel } from "../Container";
import { Reveal } from "../Reveal";
import { Clock, MessagesSquare, Scale, TrendingDown, Hourglass, AlertCircle, HelpCircle } from "lucide-react";

const icons = [Clock, MessagesSquare, Scale, TrendingDown, Hourglass];
const ease = [0.22, 1, 0.36, 1];

/** Language-neutral mini app-interface preview per problem chapter. */
function MiniVisual({ index }) {
  switch (index) {
    case 0: // late payments — overdue invoice row
      return (
        <div className="flex items-center justify-between rounded-2xl bg-black/[0.03] px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.06] text-xs font-bold text-black/50">#204</span>
            <span className="text-sm font-semibold text-black">420,00 €</span>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2.5 py-1 text-[11px] font-bold text-red-500">
            <AlertCircle className="h-3.5 w-3.5" strokeWidth={2.5} /> +14 d
          </span>
        </div>
      );
    case 1: // chasing clients — outgoing chat bubbles
      return (
        <div className="flex flex-col items-end gap-1.5">
          {[80, 64, 72].map((w, i) => (
            <span key={i} className="flex items-center gap-1 rounded-2xl rounded-br-sm bg-black/[0.04] px-3 py-2" style={{ width: `${w}%` }}>
              <span className="h-1.5 w-1.5 rounded-full bg-black/20" />
              <span className="h-1.5 flex-1 rounded-full bg-black/10" />
            </span>
          ))}
          <span className="mt-0.5 flex items-center gap-1.5 text-[11px] font-medium text-black/35">
            <MessagesSquare className="h-3.5 w-3.5" strokeWidth={2} /> 3×
          </span>
        </div>
      );
    case 2: // unclear rules — undefined condition row
      return (
        <div className="flex items-center justify-between rounded-2xl border border-dashed border-amber-400/50 bg-amber-400/[0.06] px-4 py-3">
          <span className="flex items-center gap-2 text-sm font-medium text-amber-600">
            <HelpCircle className="h-4 w-4" strokeWidth={2} /> ?
          </span>
          <span className="flex gap-1.5">
            <span className="h-2 w-8 rounded-full bg-amber-400/40" />
            <span className="h-2 w-4 rounded-full bg-black/10" />
          </span>
        </div>
      );
    case 3: // defaults & no forecast — declining bars
      return (
        <div>
          <div className="flex h-16 items-end gap-1.5">
            {[70, 52, 60, 34, 20].map((h, i) => (
              <span
                key={i}
                className={`flex-1 rounded-t-md ${i >= 3 ? "bg-red-500/70" : "bg-black/10"}`}
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
          <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-red-500">
            <TrendingDown className="h-3.5 w-3.5" strokeWidth={2.5} /> −32%
          </span>
        </div>
      );
    case 4: // opportunity cost — lost hours
    default:
      return (
        <div className="flex items-center gap-3 rounded-2xl bg-black/[0.03] px-4 py-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black/[0.06] text-black/50">
            <Hourglass className="h-4 w-4" strokeWidth={2} />
          </span>
          <div className="flex-1">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-black/[0.06]">
              <div className="h-full w-2/3 rounded-full bg-red-500/50" />
            </div>
          </div>
          <span className="text-sm font-bold text-red-500">−3 h</span>
        </div>
      );
  }
}

function ProblemCard({ chapter, Icon, index, delay, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease, delay }}
      className={`group flex h-full flex-col rounded-3xl border border-black/[0.07] bg-white p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-28px_rgba(0,0,0,0.25)] md:p-8 ${className}`}
    >
      <div className="flex items-center justify-between">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-black/[0.05] text-black transition-colors duration-300 group-hover:[background-image:linear-gradient(135deg,#08AC7D_0%,#4DD79A_100%)]">
          <Icon strokeWidth={1.75} className="h-5 w-5" />
        </span>
        <span className="font-mono text-sm text-black/25">{chapter.n}</span>
      </div>
      <h3 className="mt-6 text-xl font-semibold tracking-tight text-black">{chapter.title}</h3>
      <p className="mt-2 text-base leading-relaxed text-black/55">{chapter.text}</p>
      <div className="mt-6 pt-1">
        <MiniVisual index={index} />
      </div>
    </motion.div>
  );
}

export function Problem() {
  const { t } = useI18n();
  const chapters = t("problem.chapters");

  return (
    <section className="bg-[#F6F8F7] py-32 md:py-40">
      <Container>
        <div className="max-w-3xl">
          <SectionLabel>{t("problem.label")}</SectionLabel>
          <Reveal>
            <h2 className="text-3xl font-semibold leading-[1.1] tracking-tight text-black sm:text-4xl lg:text-5xl">
              {t("problem.title")}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg leading-relaxed text-black/55">{t("problem.intro")}</p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {chapters.slice(0, 3).map((c, i) => (
            <ProblemCard key={c.n} chapter={c} Icon={icons[i]} index={i} delay={i * 0.06} />
          ))}
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          {chapters.slice(3, 5).map((c, i) => (
            <ProblemCard key={c.n} chapter={c} Icon={icons[i + 3]} index={i + 3} delay={i * 0.06} />
          ))}
        </div>
      </Container>
    </section>
  );
}
